import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/internationalization/cn'

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required — an icon-only control is invisible to a screen reader without it. */
  label: string
  icon: ReactNode
  size?: 'sm' | 'md'
  variant?: 'ghost' | 'secondary' | 'danger'
  isActive?: boolean
}

const VARIANTS = {
  ghost: 'text-fg-muted hover:bg-surface-muted hover:text-fg',
  secondary: 'border border-border bg-surface text-fg hover:bg-surface-muted',
  danger: 'text-danger hover:bg-danger-subtle',
} as const

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, size = 'md', variant = 'ghost', isActive, className, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={isActive}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-md transition-colors duration-fast',
        'disabled:pointer-events-none disabled:opacity-50',
        size === 'sm' ? 'h-8 w-8' : 'h-10 w-10',
        VARIANTS[variant],
        isActive && 'bg-brand-subtle text-brand',
        className,
      )}
      {...props}
    >
      <span aria-hidden className={size === 'sm' ? '[&>svg]:h-4 [&>svg]:w-4' : '[&>svg]:h-[18px] [&>svg]:w-[18px]'}>
        {icon}
      </span>
    </button>
  )
})
